// frontend/src/settings/DailyRoleSettings.js
// 日次役割マスタ (リーダー / 副リーダー等) の管理ページ
// - 役割の追加・編集・削除・並び替え
// - 各役割で「最大何名」「どの勤務マークを日勤扱いとするか」を設定

import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "../supabase";
import { useOrganization } from "../contexts/OrganizationContext";
import { useInternalGuard } from "../internal/useInternalGuard";

const DailyRoleSettings = () => {
  const { currentOrganization } = useOrganization();
  const guard = useInternalGuard();
  const [roles, setRoles] = useState([]);
  const [shiftPatterns, setShiftPatterns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
    color: "#3b82f6",
    max_count: 1,
    day_shift_marks: [],
    display_order: 0,
    exclude_from_surgery: false,
  });

  const fetchAll = useCallback(async () => {
    if (!currentOrganization?.id) return;
    setLoading(true);
    try {
      const [rolesRes, patternsRes] = await Promise.all([
        supabase
          .from("daily_role_definitions")
          .select("*")
          .eq("organization_id", currentOrganization.id)
          .order("display_order"),
        supabase
          .from("shift_patterns")
          .select("id, name, short_name, configuration")
          .eq("organization_id", currentOrganization.id)
          .eq("is_active", true),
      ]);
      if (rolesRes.error) throw rolesRes.error;
      setRoles(rolesRes.data || []);
      setShiftPatterns(patternsRes.data || []);
    } catch (e) {
      console.error("daily_role_definitions 取得失敗:", e);
      setError(`取得エラー: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [currentOrganization?.id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const resetForm = () => {
    setEditingId(null);
    setForm({
      name: "", description: "", color: "#3b82f6", max_count: 1,
      day_shift_marks: [], display_order: 0, exclude_from_surgery: false,
    });
  };

  const startEdit = (role) => {
    setEditingId(role.id);
    setForm({
      name: role.name,
      description: role.description || "",
      color: role.color || "#3b82f6",
      max_count: role.max_count || 1,
      day_shift_marks: Array.isArray(role.day_shift_marks) ? role.day_shift_marks : [],
      display_order: role.display_order || 0,
      exclude_from_surgery: !!role.exclude_from_surgery,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!currentOrganization?.id) return;
    if (!form.name.trim()) return setError("役割名は必須");
    if (!guard.allow("daily_role_definitions", { op: editingId ? "update" : "insert", name: form.name })) return;
    setSaving(true);
    try {
      const payload = {
        organization_id: currentOrganization.id,
        name: form.name.trim(),
        description: form.description.trim() || null,
        color: form.color,
        max_count: Math.max(1, parseInt(form.max_count, 10) || 1),
        day_shift_marks: form.day_shift_marks,
        display_order: parseInt(form.display_order, 10) || 0,
        exclude_from_surgery: !!form.exclude_from_surgery,
      };
      if (editingId) {
        const { error: e } = await supabase.from("daily_role_definitions").update({
          ...payload,
          updated_at: new Date().toISOString(),
        }).eq("id", editingId);
        if (e) throw e;
      } else {
        const nextOrder = roles.length > 0
          ? Math.max(...roles.map((r) => r.display_order || 0)) + 1
          : 0;
        const { error: e } = await supabase.from("daily_role_definitions").insert({
          ...payload, display_order: nextOrder,
        });
        if (e) throw e;
      }
      resetForm();
      await fetchAll();
    } catch (e) {
      console.error("保存失敗:", e);
      setError(`保存エラー: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`「${role.name}」を削除しますか? (各日の割当も全て削除されます)`)) return;
    if (!guard.allow("daily_role_definitions", { op: "delete", id: role.id })) return;
    try {
      const { error: e } = await supabase.from("daily_role_definitions").delete().eq("id", role.id);
      if (e) throw e;
      await fetchAll();
    } catch (e) {
      setError(`削除エラー: ${e.message}`);
    }
  };

  const toggleMark = (mark) => {
    setForm((f) => {
      const set = new Set(f.day_shift_marks || []);
      if (set.has(mark)) set.delete(mark); else set.add(mark);
      return { ...f, day_shift_marks: [...set] };
    });
  };

  const availableMarks = shiftPatterns
    .map((p) => p.short_name)
    .filter(Boolean);

  return (
    <div style={{ padding: 20, maxWidth: 960, margin: "0 auto" }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, marginBottom: 6 }}>📌 日次役割マスタ</h1>
      <p style={{ color: "#64748b", fontSize: 13, marginTop: 0 }}>
        リーダー / 副リーダー等、その日の日勤者から選ぶ役割を任意に定義します。
        手術申込ページの日付パネルから割り当てできるようになります。
      </p>

      {error && (
        <div style={{ background: "#fee2e2", color: "#7f1d1d", padding: 10, borderRadius: 8, fontSize: 12, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {/* フォーム */}
      <form onSubmit={handleSubmit} style={{
        background: "white", borderRadius: 12, padding: 16, border: "1px solid #e5e7eb", marginBottom: 16,
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>
          {editingId ? "✏️ 役割を編集" : "➕ 新規役割を追加"}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1fr 1fr 80px", gap: 8, alignItems: "end", marginBottom: 12 }}>
          <label>
            <div style={lbl}>役割名 *</div>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="リーダー" required style={inp} />
          </label>
          <label>
            <div style={lbl}>説明</div>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="任意" style={inp} />
          </label>
          <label>
            <div style={lbl}>最大人数</div>
            <input type="number" min={1} value={form.max_count} onChange={(e) => setForm({ ...form, max_count: e.target.value })} style={inp} />
          </label>
          <label>
            <div style={lbl}>並び順</div>
            <input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: e.target.value })} style={inp} />
          </label>
          <label>
            <div style={lbl}>色</div>
            <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })}
              style={{ width: "100%", height: 32, border: "1px solid #d1d5db", borderRadius: 6 }} />
          </label>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ ...lbl, marginBottom: 6 }}>日勤扱いとする勤務パターン (複数可)</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {availableMarks.length === 0 ? (
              <div style={{ fontSize: 11, color: "#94a3b8" }}>勤務パターンが未登録です</div>
            ) : (
              availableMarks.map((mark) => {
                const checked = (form.day_shift_marks || []).includes(mark);
                return (
                  <button type="button" key={mark} onClick={() => toggleMark(mark)} style={{
                    padding: "6px 12px", borderRadius: 16, fontSize: 12, fontWeight: 600,
                    border: checked ? "2px solid #1d4ed8" : "1px solid #d1d5db",
                    background: checked ? "#dbeafe" : "white",
                    color: checked ? "#1d4ed8" : "#475569",
                    cursor: "pointer",
                  }}>
                    {checked ? "✓ " : ""}{mark}
                  </button>
                );
              })
            )}
          </div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
            選択した勤務マークが当日割り当てられているスタッフだけが、 この役割の選択肢として表示されます。
          </div>
        </div>

        {/* 🔥 手術配置から除外 */}
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, cursor: "pointer" }}>
          <input type="checkbox" checked={!!form.exclude_from_surgery}
            onChange={(e) => setForm({ ...form, exclude_from_surgery: e.target.checked })} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>各手術の人員配置候補から除外する</div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>
              例: リーダーに割り当てられた人は手術に入らない → このチェックを ON。
              当日この役割に選ばれたスタッフは、 手術ごとの器械出し/外回り候補から消えます。
            </div>
          </div>
        </label>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          {editingId && (
            <button type="button" onClick={resetForm} style={btnSecondary}>キャンセル</button>
          )}
          <button type="submit" disabled={saving} style={{
            ...btnPrimary,
            background: saving ? "#94a3b8" : btnPrimary.background,
          }}>
            {saving ? "保存中..." : (editingId ? "更新" : "追加")}
          </button>
        </div>
      </form>

      {/* 一覧 */}
      <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden" }}>
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #e5e7eb", fontSize: 14, fontWeight: 700 }}>
          登録済み役割 ({roles.length})
        </div>
        {loading ? (
          <div style={{ padding: 20, textAlign: "center", color: "#94a3b8" }}>読み込み中...</div>
        ) : roles.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
            まだ役割が定義されていません
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead style={{ background: "#f8fafc" }}>
              <tr>
                <th style={th}>並び順</th>
                <th style={{ ...th, textAlign: "left" }}>役割</th>
                <th style={{ ...th, textAlign: "left" }}>説明</th>
                <th style={th}>最大人数</th>
                <th style={{ ...th, textAlign: "left" }}>日勤マーク</th>
                <th style={th}>手術除外</th>
                <th style={th}>操作</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((r) => (
                <tr key={r.id} style={{ borderBottom: "1px solid #e5e7eb" }}>
                  <td style={td}>{r.display_order}</td>
                  <td style={{ ...td, textAlign: "left" }}>
                    <span style={{
                      padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700,
                      background: `${r.color || "#3b82f6"}22`,
                      color: r.color || "#3b82f6",
                      border: `1px solid ${r.color || "#3b82f6"}66`,
                    }}>{r.name}</span>
                  </td>
                  <td style={{ ...td, textAlign: "left", color: "#64748b" }}>{r.description || "-"}</td>
                  <td style={td}>{r.max_count} 名</td>
                  <td style={{ ...td, textAlign: "left", fontSize: 11 }}>
                    {Array.isArray(r.day_shift_marks) && r.day_shift_marks.length > 0
                      ? r.day_shift_marks.join(" / ")
                      : <span style={{ color: "#94a3b8" }}>未設定</span>}
                  </td>
                  <td style={td}>
                    {r.exclude_from_surgery
                      ? <span style={{ color: "#dc2626", fontSize: 11, fontWeight: 700 }}>✓ 除外</span>
                      : <span style={{ color: "#94a3b8", fontSize: 11 }}>-</span>}
                  </td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>
                    <button onClick={() => startEdit(r)} style={{ ...btnGhost, color: "#1d4ed8" }}>編集</button>
                    <button onClick={() => handleDelete(r)} style={{ ...btnGhost, color: "#dc2626", marginLeft: 4 }}>削除</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const lbl = { fontSize: 11, color: "#64748b", marginBottom: 4 };
const inp = { width: "100%", padding: "6px 8px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13, boxSizing: "border-box" };
const th = { padding: 8, textAlign: "center", fontWeight: 700, color: "#475569", fontSize: 11, border: "1px solid #e5e7eb" };
const td = { padding: 8, textAlign: "center", fontSize: 13, border: "1px solid #e5e7eb" };
const btnPrimary = { padding: "8px 16px", background: "#059669", color: "white", border: "none", borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: "pointer" };
const btnSecondary = { padding: "8px 16px", background: "#e5e7eb", border: "none", borderRadius: 6, fontSize: 13, cursor: "pointer" };
const btnGhost = { padding: "4px 10px", background: "transparent", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 11, cursor: "pointer" };

export default DailyRoleSettings;
